import React from 'react'
import { Link } from 'gatsby'
import {
  Layout,
  Showcase,
  Header,
  SectionTitle,
  Intro,
} from '../../../../components'

import './style.scss'
import data from './data.json'
import pic from '../../../../images/paris.jpg'

export default () => {
  return (
    <>
      <Header
        site={data.site}
        slug={data.slug + '/design/'}
        state="design"
        title={data.title}
      />
      <Layout
        slug={data.slug + '/design/'}
        site={data.site}
        state="design"
        title={data.title}
      >
        <Intro>
          <p>
            Attention cards are used to call attention to specific information
            on a page. They are used standalone to give visual distinction and
            prominence to the card content. They should provide a snapshot of
            information and entice the user to click through for further action.
          </p>
        </Intro>
        <SectionTitle>Structure</SectionTitle>
        <p>
          The attention card pattern is customizable to a number of scenarios
          and should use the design that will create the optimal experience. All
          cards in one collection should have the same basic layout and
          component parts.
        </p>
        <p>
          The only required elements within a container are the title and body
          content. The optional elements listed below provide additional
          information and visual prominence where necessary.
        </p>
        <ul class="c-unordered-list l-margin-vertical--large">
          <li class="c-unordered-list__item l-margin-left--medium">
            The <b>container</b> creates the barrier for the card information.
            It separates a card from other in a collection or, if standalone,
            from the rest of the elements on the page.
          </li>
          <li class="c-unordered-list__item l-margin-left--medium">
            An <b>image</b> or <b>yellow border bar</b> may be used as a
            spotlight to draw attention to a card on a busy page. Do not use
            both an image and a border bar.
          </li>
          <li class="c-unordered-list__item l-margin-left--medium">
            The <b>eyebrow text</b> is used to give secondary or supporting
            information. Eyebrows use the{' '}
            <Link
              class="link"
              to={'/' + data.site + '/components/title/design'}
            >
              title 4
            </Link>{' '}
            text style.
          </li>
          <li class="c-unordered-list__item l-margin-left--medium">
            The <b>Title</b> details what is contained in the card body. Titles
            use the{' '}
            <Link
              class="link"
              to={'/' + data.site + '/components/title/design'}
            >
              title 2
            </Link>{' '}
            text style.
          </li>
          <li class="c-unordered-list__item l-margin-left--medium">
            <b>Body content</b> expands on the card title and uses the default{' '}
            <Link
              class="link"
              to={'/' + data.site + '/components/text-content/design'}
            >
              text content
            </Link>{' '}
            variation.
          </li>
          <li class="c-unordered-list__item l-margin-left--medium">
            Attention cards may have up to two associated <b>actions</b> in the
            form of{' '}
            <Link
              class="link"
              to={'/' + data.site + '/components/button/design'}
            >
              buttons
            </Link>{' '}
            and{' '}
            <Link class="link" to={'/' + data.site + '/components/link/design'}>
              links
            </Link>
            . An attention card may only use a primary button if it offers the
            primary action on the page. Otherwise, cards with two actions must
            use a horizontally stacked default button and text link.
          </li>
        </ul>
        <SectionTitle>Variants</SectionTitle>
        <Showcase
          site={data.site}
          title="Standard"
          text="The standard card is offered in the DSM with removable pieces so that each card can be customized to best suit your design. The card container will respond as components are added and removed. Because of the amount of content within them, a complete standard card is not easily scannable and should rarely be used in collections."
        >
          <div class="c-card">
            <img class="c-card__img" src={pic} alt="Card" />
            <div class="c-card__body">
              <div class="c-card__eyebrow">Eyebrow Text</div>
              <h1 class="c-card__title">Header</h1>
              <p class="c-card__text">
                Card body content is concise and immediately relevant to the
                card's header.
              </p>
              <div class="c-card__button-container">
                <button class="c-card__button--primary">Action 1</button>
                <button class="c-card__button--default">Action 2</button>
                <a class="c-card__button--link" href="/#">
                  Action 3
                </a>
              </div>
            </div>
          </div>
        </Showcase>

        <Showcase
          full={true}
          site={data.site}
          title="Hero"
          text="The hero variant of the attention card is a larger, horizontal card that has all possible component parts. The image in a hero variant is more prominent as it takes up 50% of the container width. Hero cards are only used as standalones and may only be used at desktop breakpoints. At smaller breakpoints, they convert to standard cards."
        >
          <div className="card-wrapper">
            <div class="c-card--hero">
              <img class="c-card__img--hero-mobile" src={pic} alt="Card" />
              <div class="c-card__body--hero">
                <div class="c-card__eyebrow">Eyebrow Text</div>
                <h1 class="c-card__title--hero">Header</h1>
                <p class="c-card__text">
                  Card body content is concise and immediately relevant to the
                  card's header.
                </p>
                <div class="c-card__button-container--hero">
                  <button class="c-card__button--primary--hero">
                    Action 1
                  </button>
                  <button class="c-card__button--default--hero">
                    Action 2
                  </button>
                </div>
              </div>
              <img class="c-card__img--hero-desktop" src={pic} alt="Card" />
            </div>
          </div>
        </Showcase>
        <SectionTitle>Design Environment</SectionTitle>
        <p>
          Card containers are flexible and margins should adapt to the card
          size. Use more spacing in between attention cards and layout elements
          around them.
        </p>
        <SectionTitle>Microcopy</SectionTitle>
        <p>
          Because of the space restraints inherent in cards, copy should be
          concise. The relationship between a card's body content, optional
          eyebrow, title and image should be immediately apparent. Follow the
          microcopy guidelines in{' '}
          <Link class="link" to={'/' + data.site + '/components/title/design'}>
            title
          </Link>
          ,{' '}
          <Link class="link" to={'/' + data.site + '/components/button/design'}>
            buttons
          </Link>{' '}
          and{' '}
          <Link class="link" to={'/' + data.site + '/components/link/design'}>
            links
          </Link>{' '}
          for more information.
        </p>
      </Layout>
    </>
  )
}
